import { useState } from "react";
import { Link } from "wouter";
import { useQueryClient } from "@tanstack/react-query";
import {
  useListClauseUnlockRequests,
  useResolveClauseUnlockRequest,
  getListClauseUnlockRequestsQueryKey,
  getGetClauseUnlockRequestPendingCountQueryKey,
  type ClauseUnlockRequestInboxItem,
} from "@workspace/api-client-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyState } from "@/components/ui/empty-state";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { useOrg, type Persona } from "@/lib/orgContext";
import {
  CheckCircle, KeyRound, Check, Ban, ArrowRight, FileText, Lock,
} from "lucide-react";
import { formatDistanceToNow } from "date-fns";

const CATEGORY_LABELS: Record<string, string> = {
  compensation: "Compensation",
  termination: "Termination",
  buyout: "Buyout",
  nil: "NIL",
  title_ix: "Title IX",
  indemnification: "Indemnification",
  governing_law: "Governing Law",
  outside_income: "Outside Income",
  general: "General",
};

const STATUS_TABS: { value: "pending" | "approved" | "denied" | "cancelled"; label: string }[] = [
  { value: "pending", label: "Pending" },
  { value: "approved", label: "Approved" },
  { value: "denied", label: "Denied" },
  { value: "cancelled", label: "Cancelled" },
];

const STATUS_BADGE: Record<string, string> = {
  pending: "bg-amber-50 text-amber-800 border-amber-200",
  approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
  denied: "bg-red-50 text-red-700 border-red-200",
  cancelled: "bg-slate-50 text-slate-700 border-slate-200",
};

function errorMessage(err: unknown): string {
  if (err instanceof Error) return err.message;
  if (err && typeof err === "object" && "error" in err) {
    const e = (err as { error?: unknown }).error;
    if (typeof e === "string") return e;
  }
  return "Unknown error";
}

export default function UnlockRequests() {
  const { org } = useOrg();
  const personas = (org?.personas ?? []) as Persona[];
  const isAdmin = org?.role === "admin";
  const isLegal = personas.includes("Legal") || isAdmin;

  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [status, setStatus] = useState<"pending" | "approved" | "denied" | "cancelled">("pending");
  const [resolving, setResolving] = useState<{
    item: ClauseUnlockRequestInboxItem;
    action: "approve" | "deny";
  } | null>(null);
  const [note, setNote] = useState("");

  const listKey = getListClauseUnlockRequestsQueryKey({ status });
  const { data, isLoading } = useListClauseUnlockRequests(
    { status },
    {
      query: {
        queryKey: listKey,
        enabled: isLegal,
        refetchInterval: 60_000,
      },
    },
  );

  const resolveMutation = useResolveClauseUnlockRequest();

  const items = data ?? [];

  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: ["/api/clause-unlock-requests"] });
    queryClient.invalidateQueries({ queryKey: getGetClauseUnlockRequestPendingCountQueryKey() });
  };

  const submitResolve = async () => {
    if (!resolving) return;
    try {
      await resolveMutation.mutateAsync({
        id: resolving.item.id,
        data: { action: resolving.action, note: note.trim() ? note.trim() : null },
      });
      toast({
        title: resolving.action === "approve" ? "Unlock approved" : "Request denied",
        description:
          resolving.action === "approve"
            ? "The clause is now editable by drafters."
            : "The drafter will see your decision in the change history.",
      });
      setResolving(null);
      setNote("");
      invalidateAll();
    } catch (err) {
      toast({
        title: "Action failed",
        description: errorMessage(err),
        variant: "destructive",
      });
    }
  };

  if (!isLegal) {
    return (
      <div className="space-y-6 animate-fade-in">
        <div className="page-header">
          <h1>Unlock requests</h1>
          <p>Legal review queue for locked clauses across all contracts.</p>
        </div>
        <Card>
          <CardContent className="pt-6">
            <EmptyState
              icon={Lock}
              title="Legal access required"
              description="Only members with the Legal persona can review clause unlock requests."
            />
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="page-header">
          <h1>Unlock requests</h1>
          <p>Drafter requests to edit locked clauses across every contract in your org.</p>
        </div>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2">
            <KeyRound className="h-5 w-5 text-muted-foreground" />
            Clause unlock inbox
            <Badge variant="secondary" className="ml-2">
              {isLoading ? "…" : items.length}
            </Badge>
          </CardTitle>
          <CardDescription>
            Approve to unlock the clause for the drafter. Deny to keep it locked and leave a note explaining why.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Tabs value={status} onValueChange={(v) => setStatus(v as typeof status)}>
            <TabsList>
              {STATUS_TABS.map((t) => (
                <TabsTrigger key={t.value} value={t.value}>{t.label}</TabsTrigger>
              ))}
            </TabsList>
          </Tabs>

          {isLoading ? (
            <div className="space-y-2">
              {[1, 2, 3].map((i) => <Skeleton key={i} className="h-24 w-full skeleton-shimmer" />)}
            </div>
          ) : items.length === 0 ? (
            <EmptyState
              icon={CheckCircle}
              title={status === "pending" ? "Inbox zero" : "Nothing here"}
              description={
                status === "pending"
                  ? "No unlock requests are waiting on Legal review."
                  : `No ${status} unlock requests in the org history.`
              }
            />
          ) : (
            <div className="divide-y divide-border/50 -mx-2">
              {items.map((item) => (
                <UnlockRequestRow
                  key={item.id}
                  item={item}
                  isPending={status === "pending"}
                  resolving={resolveMutation.isPending && resolving?.item.id === item.id}
                  onApprove={() => { setResolving({ item, action: "approve" }); setNote(""); }}
                  onDeny={() => { setResolving({ item, action: "deny" }); setNote(""); }}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={!!resolving}
        onOpenChange={(o) => {
          if (!o) { setResolving(null); setNote(""); }
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {resolving?.action === "approve" ? "Approve unlock request" : "Deny unlock request"}
            </DialogTitle>
            <DialogDescription>
              {resolving?.action === "approve"
                ? "Approving will unlock the clause so the drafter can edit it. You can leave an optional note."
                : "Add an optional note explaining why the request is being denied. The drafter will see it in the change history."}
            </DialogDescription>
          </DialogHeader>
          {resolving && (
            <div className="space-y-3">
              <div className="rounded-md border bg-muted/30 p-3 text-sm space-y-1">
                <div className="font-medium">{resolving.item.clauseTitle}</div>
                <div className="text-xs text-muted-foreground">
                  {resolving.item.coachName}
                  <span
                    className="ml-1.5 font-mono text-[10px] text-muted-foreground/80"
                    title={`Contract ID #${resolving.item.contractId}`}
                  >
                    #{resolving.item.contractId}
                  </span>
                </div>
                <div className="text-xs text-muted-foreground italic mt-1">
                  Reason: {resolving.item.reason}
                </div>
              </div>
              <Textarea
                placeholder="Optional note for the drafter…"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={3}
              />
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setResolving(null); setNote(""); }}>
              Cancel
            </Button>
            <Button
              variant={resolving?.action === "deny" ? "destructive" : "default"}
              onClick={submitResolve}
              disabled={resolveMutation.isPending}
            >
              {resolving?.action === "approve" ? (
                <><Check className="h-4 w-4 mr-1.5" /> Approve</>
              ) : (
                <><Ban className="h-4 w-4 mr-1.5" /> Deny</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

interface RowProps {
  item: ClauseUnlockRequestInboxItem;
  isPending: boolean;
  resolving: boolean;
  onApprove: () => void;
  onDeny: () => void;
}

function UnlockRequestRow({ item, isPending, resolving, onApprove, onDeny }: RowProps) {
  const ageLabel = formatDistanceToNow(new Date(item.createdAt), { addSuffix: true });
  return (
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 py-4 px-2 hover:bg-muted/20 transition-colors rounded-lg">
      <div className="min-w-0 flex-1 space-y-1.5">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-medium text-sm truncate">{item.clauseTitle}</span>
          <Badge variant="outline" className="text-[10px] uppercase tracking-wide">
            {CATEGORY_LABELS[item.clauseCategory] ?? item.clauseCategory}
          </Badge>
          <Badge variant="outline" className={`text-[10px] uppercase ${STATUS_BADGE[item.status] ?? ""}`}>
            {item.status}
          </Badge>
        </div>
        <div className="text-xs text-muted-foreground">
          <Link href={`/contracts/${item.contractId}`} className="font-medium hover:underline">
            {item.coachName}
          </Link>
          <span
            className="ml-1.5 font-mono text-[10px] text-muted-foreground/80"
            title={`Contract ID #${item.contractId}`}
          >
            #{item.contractId}
          </span>
          {" · requested by "}
          <span className="font-medium">{item.requestedByName ?? "Unknown"}</span>
          {" · "}
          {ageLabel}
        </div>
        <div className="text-xs text-foreground/80 italic line-clamp-2">
          “{item.reason}”
        </div>
        {item.status !== "pending" && item.resolvedByName && (
          <div className="text-[11px] text-muted-foreground">
            Resolved by {item.resolvedByName}
            {item.resolutionNote ? ` — ${item.resolutionNote}` : ""}
          </div>
        )}
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <Button asChild variant="outline" size="sm">
          <Link href={`/contracts/${item.contractId}`}>
            <FileText className="h-3.5 w-3.5 mr-1" />
            Open contract
            <ArrowRight className="h-3.5 w-3.5 ml-1" />
          </Link>
        </Button>
        {isPending && (
          <>
            <Button size="sm" variant="outline" onClick={onDeny} disabled={resolving}>
              <Ban className="h-3.5 w-3.5 mr-1" /> Deny
            </Button>
            <Button size="sm" onClick={onApprove} disabled={resolving}>
              <Check className="h-3.5 w-3.5 mr-1" /> Approve
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
